import moment from 'moment';
import storeManager from '../store/StoreManager';
import assessmentActions from '../actions/AssessmentActions';
import navigationService from './NavigationService';
import settingsActions from '../actions/SettingsActions';
import pendingAssessmentsActions from '../actions/PendingAssessmentsActions';
import apiService from './api/ApiService';
import sessionActions from '../actions/SessionActions';
import sessionConstants from '../constants/SessionConstants';
import utilService from './UtilService';
import timeService from './TimeService';
import parentTestActions from '../actions/ParentTestActions';
import testStatusConstants from '../constants/TestStatusConstants';
import { getSystemTimeZone } from './DateTimeService';
import environmentNames from '../environmentNames';
import candidateProgressConstants from '../constants/CandidateProgressConstants';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import backgroundAudioService from './BackgroundAudioService';
import sessionReader from '../store/readHelpers/SessionReader';
import stateCacheService from './StateCache';
import awsIOTService from './AWSIOTService';
import apiReader from '../store/readHelpers/ApiReader';
import { parseDocumentAssessmentItem } from '../qti/parser/question/QTIAssessmentItem';
import stateRestorationService from './StateRestoration';
import fileDeliveryService from './FileDeliveryService';
import fileDeliveryUploader from './FileDeliveryUploader';
import responseReader from '../store/readHelpers/ResponseReader';
import responseActions from '../actions/ResponseActions';
import deliveringActions from '../actions/DeliveringActions';
import apiActions from '../actions/ApiActions';
import messagesService from './MessagesService';
import { cacheResultTypes, cacheStatusTypes } from '../actions/CacheStatusActions';
import cacheManager from '../loading/resources/cacheManager';
import settingsReader from '../store/readHelpers/SettingsReader';
import idleWarningService from './IdleWarningService';
import readinessCheckService from './ReadinessCheckService';
import progressActions from '../actions/ProgressActions';
import catActions from '../actions/CatActions';
import testComponentService from './TestComponentService';
import preCheckService from './PreCheckService';
import reloginService from './ReloginService';
import clientEventType from '../constants/ClientEventType';
import marketplacePropertiesService from './MarketplacePropertiesService';

const LAST_MINUTES_MILLISECONDS = 5 * 60 * 1000;
const COUNTDOWN_UPDATE_INTERVAL = 20 * 1000;
const COUNTDOWN_UPDATE_FROM_SERVER_INTERVAL = 60 * 1000;

export function mergeParentChildTestInfo(master, overrides) {
    const merged = JSON.parse(JSON.stringify(master));
    merged.childTests = merged.childTests
        .map((childTest) => {
            const override = overrides.childTests.find(oChild => oChild.assessmentRunId === childTest.assessmentRunId);
            childTest.descriptionDocumentApproved = override.descriptionDocumentApproved || false;
            childTest.deliveredOffline = override.deliveredOffline || false;
            childTest.startedOffline = override.startedOffline || false;
            return childTest;
        });

    return merged;
}

const parentTestService = {
    async loadParentTestQuestionSet() {
        const parentQuestionSet = await apiService.fetchQuestionSet();

        if (parentQuestionSet) {
            storeManager.dispatch(
                parentTestActions.setParentTestQuestionSet(parentQuestionSet)
            );
        }
    },

    async loadParentTestChildTests() {
        try {
            this.unsetChildIdentifiers();
            const parentChildTest = await apiService.fetchChildTests();
            const parsedParentChildTest = this.parseDescriptionDocuments(parentChildTest);
            const stateCacheInstance = stateCacheService.getStateCacheInstance();
            const localParentChildTestInfo = stateCacheInstance.getParentChildTestInfo();

            if (localParentChildTestInfo) {
                const mergedParentChildTestInfo = mergeParentChildTestInfo(parsedParentChildTest, localParentChildTestInfo);
                stateCacheInstance.setParentChildTestInfo(mergedParentChildTestInfo);
                storeManager.dispatch(
                    parentTestActions.childTestsLoadSuccess(mergedParentChildTestInfo),
                );
            } else {
                stateCacheInstance.setParentChildTestInfo(parsedParentChildTest);
                storeManager.dispatch(
                    parentTestActions.childTestsLoadSuccess(parsedParentChildTest),
                );
            }
            assessmentActions.setWindowTitle(parsedParentChildTest.title);
        } catch (error) {
            storeManager.dispatch(
                parentTestActions.childTestsLoadFailed(error),
            );
        }
    },

    parseDescriptionDocument(descriptionDocument, descriptionDocumentMediaInfo, assessmentRunId) {
        if (descriptionDocument.length > 0) {
            const unescapedDescriptionDocument = descriptionDocument.replace(/\\"/g, '"');
            return parseDocumentAssessmentItem({
                content: unescapedDescriptionDocument,
                mediaInfo: descriptionDocumentMediaInfo,
                identifier: `childDescDoc__${assessmentRunId}`,
                displayContext: 'child-desc-document',
            }, undefined, assessmentRunId);
        }

        return undefined;
    },

    parseDescriptionDocuments(parentTest) {
        const childTestsClone = parentTest.childTests.map((childTest) => {
            if (childTest.descriptionDocument.length > 0) {
                const documentModel = this.parseDescriptionDocument(
                    childTest.descriptionDocument,
                    childTest.descriptionDocumentMediaInfo,
                    childTest.assessmentRunId,
                );

                const test = { ...childTest, documentModel };
                // indicates that the description has at least one media that has been played to completion
                // also will be true if media was just started but not played to completion in previous session
                test.hasAtLeastOneCompletelyPlayedMedia = testComponentService.hasAtLeastOnePlayedMedia(test);
                return test;
            }

            return childTest;
        });

        return { ...parentTest, childTests: childTestsClone };
    },

    setChildTestDescriptionDocumentApproved(assessmentRunId) {
        const stateCacheInstance = stateCacheService.getStateCacheInstance();
        storeManager.dispatch(
            parentTestActions.confirmDescriptionDocument(assessmentRunId)
        );

        const parentChildTestInfo = storeManager.getState().parentTest;
        stateCacheInstance.setParentChildTestInfo(parentChildTestInfo);
    },

    async startChildTest(assessmentRunId, state = storeManager.getState()) {
        const { assessment, pendingAssessments } = state;
        this.stopParentTestIntervals();
        if (settingsReader.isProgressBarQuestionsEnabled()) {
            const store = storeManager.getStore();
            store.dispatch(
                progressActions.setFooterProgressBarValue(0)
            );
        }
        if (assessmentRunId in pendingAssessments) {
            this.setCurrentAssessmentItem(assessmentRunId);
            this.setChildAssessmentRunId(assessmentRunId);
            if (apiReader.isOnline()) {
                await this.startChildServices(assessmentRunId);
            } else {
                storeManager.dispatch(parentTestActions.setChildTestStartedOffline(assessmentRunId));
            }
        } else if (assessment.assessmentRunId === assessmentRunId) {
            this.setChildAssessmentRunId(assessmentRunId);
            if (apiReader.isOnline()) {
                await this.startChildServices(assessmentRunId);
            } else {
                storeManager.dispatch(parentTestActions.setChildTestStartedOffline(assessmentRunId));
            }
        }
    },

    startReadinessCheck(contentRevisionId, state = storeManager.getState()) {
        const { pendingAssessments } = state;
        this.stopParentTestIntervals();
        if (contentRevisionId in pendingAssessments) {
            this.setCurrentAssessmentItem(contentRevisionId);
            readinessCheckService.setChildContentRevisionId(contentRevisionId);
        }
        this.clearLoadWaitTimer(contentRevisionId);
    },

    setCurrentAssessmentItem(identifier, state = storeManager.getState()) {
        const { pendingAssessments } = state;
        const marketplaceProperties = window.marketplaceProperties || {};
        const allSettings = {
            ...pendingAssessments[identifier].settings,
            marketplaceProperties,
        };
        storeManager.dispatch(
            settingsActions.settingsLoadSuccess(allSettings)
        );
        storeManager.dispatch(
            assessmentActions.assessmentLoadSuccess(pendingAssessments[identifier].assessmentItem)
        );
        storeManager.dispatch(
            pendingAssessmentsActions.removePendingAssessment(identifier)
        );
    },

    startAndNavigateInProgress: false,
    async startChildTestAndNavigate(assessmentRunId, notifyParentTestProgress = false) {
        if (this.startAndNavigateInProgress) {
            console.warn('Attempting to start a test in progress', assessmentRunId);
            return;
        }
        try {
            if (sessionReader.shouldDelegateClipboardClearingToIEP()) {
                await apiService.sendIEPClipboardClearingRequest();
            } else {
                utilService.clearClipboard();
            }
            this.startAndNavigateInProgress = true;

            if (notifyParentTestProgress) {
                this.notifyCandidateProgressToClient(candidateProgressConstants.IN_PROGRESS);
            }

            stateCacheService.getStateCacheInstance().setParentTime(storeManager.getState().time);
            await this.startChildTest(assessmentRunId);
            const onStartChildTime = Date.now();

            if (sessionReader.isChildResumed()) {
                await Promise.all([
                    stateRestorationService.loadSavedResponsesForExam(),
                    stateRestorationService.restoreSavedQuestionFlags(),
                    messagesService.fetchCompareAndAlertMessages(true),
                ]);
            }

            if (sessionReader.isAutoDeliverHighStakesEnabled() && storeManager.getState().time.timeRemainingFetchedFromServer === 0) {
                console.info('No time left for TT', assessmentRunId);
                // If time remaining is 0 and auto deliver is configured, there is no point in navigating to the first page
                // as this leads to the auto deliver route being activated and user taken back to the test overview when done.
                this.startAndNavigateInProgress = false;
                this.logStartChildTestTime(onStartChildTime, assessmentRunId);
                return;
            }

            console.info('Before navigateToFirstPage', assessmentRunId);

            // if relogin rules context, try to navigate the TT to last visited question
            const didAutoNavigateToLastVisitedPage = reloginService.isResumedTestWithReloginRules() &&
                reloginService.navigateAfterChildTestStartAfterRelogin();
            if (!didAutoNavigateToLastVisitedPage && reloginService.isResumedTestWithReloginRules()) {
                console.warn(`Failed to navigate TT after relogin on child test: ${assessmentRunId} - with relogin rules enabled`);
            }

            // if navigation is locked
            if (!didAutoNavigateToLastVisitedPage && !navigationService.navigateToFirstPage()) {
                this.startAndNavigateInProgress = false;
                this.logStartChildTestTime(onStartChildTime, assessmentRunId);
                return;
            }

            if (assessmentReader.hasBackgroundAudio(assessmentRunId) && !sessionReader.isBackgroundAudioControlAllowed()) {
                backgroundAudioService.start(assessmentRunId);
            }

            if (settingsReader.isIdleStateWarningEnabled()) {
                idleWarningService.initialize();
            }

            this.logStartChildTestTime(onStartChildTime, assessmentRunId);
        } catch (e) {
            console.error('Starting child test and navigate failed, going back to test overview', e);
            this.unsetChildIdentifiers();
            this.startStartTimeCountdown();
        }
        this.startAndNavigateInProgress = false;
    },

    async toggleIIBExitButton(isOnline) {
        if (!marketplacePropertiesService.isDisableSaveSubmissionFile()) return;

        if (isOnline) {
            await apiService.sendEnableExitUrl();
        } else {
            await apiService.sendDisableExitUrl();
        }
    },

    logStartChildTestTime(startTime, assessmentRunId) {
        const delay = Math.ceil((Date.now() - startTime) / 1000);
        if (delay > 3) {
            console.warn(`Test took ${delay}s to finalise startup after start, assessmentRunId - ${assessmentRunId}`);
        }
    },

    async startChildServices(assessmentRunId) {
        const { resumed, sessionInfo } = await apiService.startTest(false, false);
        storeManager.dispatch(sessionActions.setChildSessionInfo(sessionInfo));
        storeManager.dispatch(sessionActions.setChildResumed(resumed));
        await timeService.start(assessmentRunId);
        if (awsIOTService.allowIOTConnection()) {
            // setup IOT sockets when child test is started to avoid push channel messup
            awsIOTService.subscribeChildTopics();
        }
        this.clearLoadWaitTimer(assessmentRunId);
    },

    async startParentServices() {
        await timeService.start(sessionReader.getAssessmentRunId(true));
    },

    finishCurrentChildTest(notifyParentTestProgress = false) {
        const state = storeManager.getState();
        const { title } = state.parentTest;
        if (assessmentReader.hasBackgroundAudioResource()) {
            backgroundAudioService.stopAndRemoveAudio();
        }

        if (settingsReader.isIdleStateWarningEnabled()) {
            idleWarningService.destroy();
        }

        if (awsIOTService.allowIOTConnection()) {
            // setup IOT sockets when child test is started to avoid push channel messup
            awsIOTService.unsubscribeChildTopics();
        }

        this.unsetChildIdentifiers();
        if (!settingsReader.isPciBasedCustomAssessment()) {
            storeManager.dispatch(assessmentActions.setAssessmentState({}));
        }

        const store = storeManager.getStore();
        store.dispatch(sessionActions.setAnnotationsSidebarOpen(false));

        assessmentActions.setWindowTitle(title);

        if (settingsReader.isCatTestType()) {
            store.dispatch(
                catActions.resetCurrentCatRevisionId()
            );
        }

        if (notifyParentTestProgress) {
            this.notifyCandidateProgressToClient(candidateProgressConstants.READY);
        }
    },

    setChildAssessmentRunId(assessmentRunId) {
        storeManager.dispatch(
            sessionActions.setChildTestIdentifiers({
                childTestIdentifier: {
                    type: 'questionSet',
                    paramName: 'assessmentRunId',
                    paramValue: assessmentRunId,
                },
                childContext: sessionConstants.CONTEXT_EXAM,
            })
        );
        utilService.addUrlParams({
            childAssessmentRunId: assessmentRunId,
            childContext: sessionConstants.CONTEXT_EXAM,
        });
    },

    unsetChildIdentifiers() {
        storeManager.dispatch(
            sessionActions.unsetChildTestIdentifiers()
        );
        utilService.removeUrlParams('childAssessmentRunId', 'childContentRevisionId', 'childContext');
    },

    notifyCandidateProgressToClient(progress) {
        apiService.notifyClientEvent(progress, '', true);
    },

    timeUntilStart(test) {
        const startTime = moment.utc(test.startTime, sessionConstants.SIMPLE_DATE_FORMAT);
        const currentTime = moment.tz !== undefined
            ? moment(utilService.getCurrentTimeStamp()).tz(getSystemTimeZone())
            : moment(utilService.getCurrentTimeStamp());
        return moment.duration(startTime.diff(currentTime)).asMilliseconds();
    },

    minutesUntilStart(test) {
        const millisecondsUntilStart = this.timeUntilStart(test);
        return Math.max(0, Math.ceil(millisecondsUntilStart / (60 * 1000)));
    },

    startLastMinutesCountdownMode() {
        const time = this.timeUntilStart(this.firstScheduledChildTest);
        const timeout = time - COUNTDOWN_UPDATE_INTERVAL;
        if (process.env.NODE_ENV !== environmentNames.DEVELOPMENT) {
            clearTimeout(this.loadFirstScheduledTestTimeout);
            this.loadFirstScheduledTestTimeout = setTimeout(() => this.loadParentTestChildTests(), timeout);
        }

        clearInterval(this.updateInterval);
        this.updateInterval = setInterval(
            () => {
                const newTime = this.timeUntilStart(this.firstScheduledChildTest);
                const minutes = this.minutesUntilStart(this.firstScheduledChildTest);
                if (newTime < 0) {
                    clearInterval(this.updateInterval);
                    if (process.env.NODE_ENV !== environmentNames.DEVELOPMENT) {
                        this.loadParentTestChildTests();
                    }
                }
                this.setMinutesUntilChildStartTime(minutes);
            }, 1000
        );
    },

    startNormalCountdownMode() {
        this.updateInterval = setInterval(
            () => {
                const time = this.timeUntilStart(this.firstScheduledChildTest);
                const minutes = this.minutesUntilStart(this.firstScheduledChildTest);
                if (time < LAST_MINUTES_MILLISECONDS) {
                    this.startLastMinutesCountdownMode();
                }
                this.setMinutesUntilChildStartTime(minutes);
            }, COUNTDOWN_UPDATE_INTERVAL
        );
    },

    startStartTimeCountdown(state = storeManager.getState()) {
        const { childTests } = state.parentTest;
        this.firstScheduledChildTest = childTests.find(test => test.status === testStatusConstants.SCHEDULED);
        this.startFetchChildTestsInterval();
        if (!this.firstScheduledChildTest) {
            return;
        }
        const time = this.timeUntilStart(this.firstScheduledChildTest);
        const minutes = this.minutesUntilStart(this.firstScheduledChildTest);
        this.setMinutesUntilChildStartTime(minutes);

        if (time < LAST_MINUTES_MILLISECONDS) {
            this.startLastMinutesCountdownMode();
        } else if (time > LAST_MINUTES_MILLISECONDS) {
            this.startNormalCountdownMode();
        }
    },

    startFetchChildTestsInterval() {
        this.fetchInterval = setInterval(
            () => {
                if (process.env.NODE_ENV !== environmentNames.DEVELOPMENT) {
                    this.loadParentTestChildTests();
                }
            }, COUNTDOWN_UPDATE_FROM_SERVER_INTERVAL
        );
    },

    stopParentTestIntervals() {
        clearInterval(this.updateInterval);
        clearInterval(this.fetchInterval);
        clearTimeout(this.loadFirstScheduledTestTimeout);
        this.updateInterval = undefined;
        this.fetchInterval = undefined;
        this.loadFirstScheduledTestTimeout = undefined;
    },

    setMinutesUntilChildStartTime(minutesUntilStartTime) {
        storeManager.dispatch(
            parentTestActions.updateMinutesUntilChildStartTime(minutesUntilStartTime)
        );
    },

    getSettingsForPendingTest(assessmentRunId) {
        const pending = storeManager.getState().pendingAssessments[assessmentRunId];
        if (pending === undefined) {
            return undefined;
        }

        return pending.settings;
    },

    isChildTestCompleted(childTest, offlineFileDeliveryStatus) {
        const fileDeliveryStatus = (offlineFileDeliveryStatus[childTest.assessmentRunId] || {}).status;
        return childTest.status === testStatusConstants.FINISHED
            || childTest.deliveredOffline
            || fileDeliveryStatus === deliveringActions.statuses.COMPLETE;
    },

    getAllPendingAssessmentRunIds() {
        return Object.keys(storeManager.getState().pendingAssessments);
    },

    getChildOrParentAssessment(assessmentRunId) {
        const { assessment, pendingAssessments } = storeManager.getState();
        if (assessment.assessmentRunId === assessmentRunId) {
            return assessment;
        }
        return pendingAssessments[assessmentRunId]?.assessmentItem;
    },

    getChild(childAssessmentRunId) {
        const { childTests } = storeManager.getState().parentTest;
        if (!childTests) {
            return null;
        }

        return childTests.find(test => test.assessmentRunId === childAssessmentRunId);
    },

    hasChild(childAssessmentRunId) {
        return !!this.getChild(childAssessmentRunId);
    },

    verifyParentChildContext() {
        if ((sessionReader.isParentTestContextOutsideChildTest() && !sessionReader.isContextReadinessCheck())
            && !settingsReader.isPciBasedCustomAssessment()) {
            const { currentlyShown } = storeManager.getState();
            if (currentlyShown.questionId || currentlyShown.sectionId) {
                console.error('Parent child context is wrong.', currentlyShown);
            }
        }
    },

    handleOfflineAutoDeliveryOfChildTest(assessmentRunId) {
        // reset response sending queue as remaining responses will be stored in offline delivery
        storeManager.dispatch(apiActions.setUserResponseQueue([]));
        const deliveryObject = fileDeliveryService.getDeliveryObject(false, assessmentRunId);
        stateCacheService
            .getStateCacheInstance()
            .setDeliveryObject(assessmentRunId, deliveryObject);
        stateCacheService.getStateCacheInstance().setDeliveryStatus(assessmentRunId, deliveringActions.statuses.PENDING);
        storeManager.dispatch(deliveringActions.setOfflineFileDeliveryObject(assessmentRunId, deliveryObject));
        storeManager.dispatch(deliveringActions.setOfflineFileDeliveryStatus(assessmentRunId, deliveringActions.statuses.PENDING));
        fileDeliveryUploader.addToQueue(assessmentRunId);
    },

    deleteChildTestResponses(assessmentRunId) {
        const toRemove = responseReader
            .getResponsesForAssessmentRunId(assessmentRunId)
            .map(response => response.identifier);

        storeManager.dispatch(responseActions.removeResponses(toRemove));
        toRemove
            .forEach((questionId) => {
                stateCacheService
                    .getStateCacheInstance()
                    .removeQuestionResponse(questionId);
            });
    },

    loadIntervals: {},

    startLoadWaitTimer(id, maxMinutesWait, notifyOnTimeout) {
        if (this.loadIntervals[id]) {
            return;
        }
        this.loadIntervals[id] = setInterval(() => {
            this.clearLoadWaitTimer(id);
            if (!cacheManager.cacheIsDone(id)) {
                if (notifyOnTimeout) {
                    apiService.notifyClientEvent(clientEventType.CLIENT_RESOURCE_LOAD_TIMEOUT, '', true, id);
                }
                console.error('Resource load timeout - ID:', id);
                cacheManager.reportCacheStatus(id, cacheStatusTypes.DONE, cacheResultTypes.TIMEOUT);
                cacheManager.abortResourceLoading();
            }
        }, maxMinutesWait * 60 * 1000);
    },

    clearLoadWaitTimer(id) {
        clearInterval(this.loadIntervals[id]);
    },

    getInProgressChildTest() {
        if (sessionReader.isParentTest()) {
            const state = storeManager.getState();
            return state.parentTest.childTests.find(childTest => childTest.status === testStatusConstants.EXECUTING &&
                !!childTest.deliveryStartTimeMillis && childTest.deliveryStartTimeMillis > 0);
        }
        return undefined;
    },

    startInProgressChildTestIfTestCached(cachedAssessmentItem) {
        if (reloginService.isReloginRedirectAllowed()) {
            const inProgressChildTest = parentTestService.getInProgressChildTest();
            if (inProgressChildTest && inProgressChildTest.assessmentRunId === cachedAssessmentItem.assessmentRunId &&
                preCheckService.readinessCheckCompleted()) {
                console.log(`calling startChildTestAndNavigate in startInProgressChildTestIfTestCached for 
                child test: ${inProgressChildTest.assessmentRunId}`);
                parentTestService.startChildTestAndNavigate(inProgressChildTest.assessmentRunId, inProgressChildTest.orderNumber >= 1);
            }
        }
    },

    startInProgressChildTestAfterReadinessCheckCompleted() {
        const inProgressChildTest = parentTestService.getInProgressChildTest();
        if (inProgressChildTest && cacheManager.cacheIsDone(inProgressChildTest.assessmentRunId)) {
            console.log(`calling startChildTestAndNavigate in startInProgressChildTestAfterReadinessCheckCompleted for 
            child test: ${inProgressChildTest.assessmentRunId}`);
            return parentTestService.startChildTestAndNavigate(inProgressChildTest.assessmentRunId, inProgressChildTest.orderNumber >= 1);
        }
        return navigationService.navigateToTestOverview();
    },

    startInProgressChildTestIfReadinessCheckCompletedAndTestCached() {
        const inProgressChildTest = parentTestService.getInProgressChildTest();
        if (inProgressChildTest && cacheManager.cacheIsDone(inProgressChildTest.assessmentRunId) && preCheckService.readinessCheckCompleted()) {
            console.log(`calling startChildTestAndNavigate in startInProgressChildTestIfReadinessCheckCompletedAndTestCached for 
            child test: ${inProgressChildTest.assessmentRunId}`);
            return parentTestService.startChildTestAndNavigate(inProgressChildTest.assessmentRunId, inProgressChildTest.orderNumber >= 1);
        }
        return navigationService.navigateToTestOverview();
    },

    getParentAssessmentRunId() {
        const { assessmentRunId } = storeManager.getState().parentTest;
        return assessmentRunId || null;
    },

    getChildTestsAssessmentRunId() {
        const { childTests } = storeManager.getState().parentTest;
        const assessmentRunIds = childTests?.map(test => test.assessmentRunId);
        return assessmentRunIds || [];
    },

    getAvailableAssessmentRunId() {
        const state = storeManager.getState();
        const currentAssessmentRunId = state?.assessment?.assessmentRunId;
        const parentAssessmentRunId = this.getParentAssessmentRunId();
        const childTestsAssessmentRunId = this.getChildTestsAssessmentRunId();
        const inProgressChildTest = this.getInProgressChildTest();
        const inProgressChildRunId = inProgressChildTest?.assessmentRunId;

        // 1. Use current test's run ID if it exists
        if (currentAssessmentRunId) return currentAssessmentRunId;

        // 2. If a child test is in progress, and its ID is in the list, use it
        if (inProgressChildRunId && childTestsAssessmentRunId.includes(inProgressChildRunId)) {
            return inProgressChildRunId;
        }

        // 3. Otherwise, fall back to parent
        return parentAssessmentRunId;
    },
};

export default parentTestService;
